import type { Dispatch } from 'redux';
import type { Location } from 'history';
import {
  SET_LOCALE,
  FETCH_PAGE_REQUEST,
  FETCH_PAGE_SUCCESS,
  FETCH_PAGE_FAILURE,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILURE,
  UNLOAD_PAGE,
} from './types';
import {
  HOMEPAGE,
  LANDING_PAGE,
  SEARCH,
  HOST,
  BOOKING,
  BOOKING_CHECK,
  BOOKING_CONFIRMATION,
  REVIEWS_NEW,
  REVIEWS_SUCCESS,
  ABOUT,
  PageTypes,
} from '../config/pageTypes';
import ApiClient from '../../utils/ApiClient';
import { fetchCreateReview } from '../../features/reviews/reviewApiClient';
import { receiveNewReview } from '../../features/reviews/actions';
import {
  resetAccommodationReviews,
  loadAccommodationReviews,
} from '../../features/accommodationReviews/actions';
import { getRequestQueryString } from '../../features/request/helpers';

export function setLocale(locale: string): { type: string; locale: string } {
  return {
    type: SET_LOCALE,
    locale,
  };
}

function requestPage(
  locale: string,
  pageType: string,
  params: any,
  location: Location,
): {
  type: string;
  loading: boolean;
  locale: string;
  pageType: string;
  params: any;
  location: Location;
} {
  return {
    type: FETCH_PAGE_REQUEST,
    loading: true,
    locale,
    pageType,
    params,
    location,
  };
}

function receivePage(pageType: string, data: any) {
  return (dispatch: Dispatch): void => {
    dispatch({
      type: FETCH_PAGE_SUCCESS,
      loading: false,
      pageType,
      data,
    });
  };
}

function failPage(): { type: string; loading: boolean } {
  return {
    type: FETCH_PAGE_FAILURE,
    loading: false,
  };
}

function receiveUser(data: any): { type: string; loading: boolean; data: any } {
  return {
    type: FETCH_USER_SUCCESS,
    loading: false,
    data,
  };
}

function failUser(): { type: string; loading: boolean } {
  return {
    type: FETCH_USER_FAILURE,
    loading: false,
  };
}

function fetchUserSession(lang: string) {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const res = await ApiClient.getUserSession(lang);
      dispatch(receiveUser(res.data));
    } catch {
      dispatch(failUser());
    }
  };
}

export function unloadPage(routeName: string): { type: string; routeName: string } {
  return {
    type: UNLOAD_PAGE,
    routeName,
  };
}

export function fetchPage(lang: string, pageType: PageTypes, params: any, location: Location) {
  return async (dispatch: Dispatch): Promise<void> => {
    if (pageType != null) {
      dispatch(requestPage(lang, pageType, params, location));
    }

    let ajaxPromise;
    switch (pageType) {
      case HOMEPAGE:
        ajaxPromise = ApiClient.getHomepage(lang);
        break;
      case LANDING_PAGE:
        ajaxPromise = ApiClient.getLandingPage(lang, params.slug);
        break;
      case SEARCH:
        ajaxPromise = ApiClient.getSearchResults(lang, getRequestQueryString());
        break;
      case HOST:
        dispatch(resetAccommodationReviews());
        ajaxPromise = ApiClient.getHostPage(lang, params.host);
        dispatch(loadAccommodationReviews(lang, params.host, 1) as any);
        break;
      case BOOKING:
        ajaxPromise = ApiClient.postProcessingBooking(
          lang,
          location.state && (location.state as any).body,
        );
        break;
      case BOOKING_CHECK: {
        const formData = location.state && (location.state as any).body;
        ajaxPromise = ApiClient.postProcessingCheckCosts(lang, {
          booking_information: {
            accommodation_id: formData.accommodation_id,
            arrival: formData.arrival,
            duration_of_stay: formData.duration_of_stay,
            number_of_adults: formData.number_of_adults,
            number_of_children: formData.number_of_children,
            number_of_infants: formData.number_of_infants,
            number_of_pets: formData.number_of_pets,
            group_max: formData.group_max,
            group_min: formData.group_min,
            group_comment: formData.group_comment,
            insurance_country: formData.insurance_country,
            comment: formData.booking_comment,
            payment_method: formData.payment_method,
            booking_id: formData.booking_id,
            amount: formData.amount,
            insurance_terms_of_service: formData.insurance_terms_of_service ? '1' : '0',
            add_cancellation_insurance: !!formData.add_cancellation_insurance,
          },
          login_free_user: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            invoice_street: formData.invoice_street,
            invoice_postal_code: formData.invoice_postal_code,
            invoice_city: formData.invoice_city,
            invoice_phone: formData.invoice_phone,
            invoice_mobile_phone: formData.invoice_mobile_phone,
            email: formData.email,
            newsletter: formData.newsletter ? '1' : '0',
            terms_of_service: formData.terms_of_service ? '1' : '0',
          },
          max_persons: formData.max_persons,
          current_persons: formData.current_persons,
        });
        break;
      }
      case BOOKING_CONFIRMATION:
        ajaxPromise = ApiClient.postProcessingReserve(lang);
        break;
      case REVIEWS_NEW: {
        const res = await fetchCreateReview(lang, params.invitationCode);
        dispatch(fetchUserSession(lang) as any);
        dispatch(receivePage(pageType, {}) as any);
        dispatch(receiveNewReview(res));
        return;
      }
      case REVIEWS_SUCCESS:
      case ABOUT:
        dispatch(receivePage(pageType, {}) as any);
        return;
      default:
        dispatch(failPage());
        return;
    }

    try {
      const res = await ajaxPromise;
      dispatch(fetchUserSession(lang) as any);
      dispatch(receivePage(pageType, res.data) as any);
    } catch {
      dispatch(failPage());
    }
  };
}
